import type { ShipmentTrackingSummary } from 'common/src/utils/types';
import { addUniqueShipmentIdentifier } from '../shipment-identifiers.ts';
import { getShipmentLogisticsStatus } from '../shipment-logistics.ts';
import type {
  CommercialPlanSectionData,
  LabelFormatMatrix,
  LabelFormatRow,
  RecentShipmentsData,
  ReportPeriod,
  ShipmentReportBucket,
  ShipmentReportPort,
  ShipmentReportSplit,
  ShipmentReportVariety,
  ShipmentTransport,
  ShipmentsToDateData,
  UpcomingArrival,
  UpcomingArrivalsData,
  UpcomingDay,
} from './types.ts';

// Arma las secciones del informe de despachos con las mismas reglas que la
// pantalla de Despachos: un embarque es una referencia, el estado logístico
// sale del tracking (o de ETD/ETA si no hay) y las cajas equivalentes son
// kilos / peso de la caja equivalente de la especie. Solo cuenta lo que ya
// zarpó: lo que está por despachar no entra.

export type ReportShipmentBox = {
  reference_number?: string | null;
  receiver?: string | null;
  exporter_receiver_id?: number | null;
  quantity?: number | null;
  box_weight?: number | null;
  variety?: string | null;
  labeled_variety?: string | null;
  caliber?: string | null;
  box_label?: string | null;
  box_format?: string | null;
  container_number?: string | null;
  awb_number?: string | null;
  ship_name?: string | null;
  ship_type?: string | null;
  ship_etd?: string | null;
  ship_eta?: string | null;
  ship_destination_port?: string | null;
  ship_destination_country?: string | null;
  tracking_journeys?: ShipmentTrackingSummary[];
};

const WITHOUT_REFERENCE = 'SIN REFERENCIA';
const MAX_VARIETIES = 6;
const MAX_FORMATS = 5;
// Orden de los calibres si la especie no trae el suyo; el mismo que usa el
// filtro de calibres. Los que no están en la lista van al final.
export const DEFAULT_CALIBER_ORDER = ['L', 'XL', 'J', '2J', '3J', '4J', '5J'];
// Días hacia adelante de "Arribos próximos 7 días".
const UPCOMING_DAYS = 7;
const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000;

const toDay = (value?: string | null) => (value ? value.slice(0, 10) : null);

export const localDay = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
    date.getDate()
  ).padStart(2, '0')}`;

export const daysBetween = (from: string, to: string) =>
  Math.round(
    (Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) /
      MILLISECONDS_PER_DAY
  );

// Las exportadoras escriben el texto de distintas formas; "NO DEFINIDO" viene
// de Frutic cuando falta el dato.
const cleanText = (value?: string | null) => {
  const text = (value || '').replace(/\s+/g, ' ').trim();
  return text && !/^(NO DEFINIDO|NA|N\/A|-)$/i.test(text) ? text : null;
};

// El tipo de transporte es opcional y viene escrito de muchas formas (A,
// AEREO, AIR, MARITIMO, SEA…). Si no se reconoce, queda sin tipo.
export const normalizeTransport = (
  value?: string | null
): ShipmentTransport | null => {
  const text = (value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toUpperCase();
  if (!text) return null;
  if (text === 'A' || text === 'M' || text === 'T') return text;
  if (/AERE|AIR/.test(text)) return 'A';
  if (/MARIT|SEA|OCEAN/.test(text)) return 'M';
  if (/TERRES|EARTH|TRK|TRUCK/.test(text)) return 'T';
  return null;
};

export const filterBoxesByPeriod = <T extends ReportShipmentBox>(
  boxes: T[],
  period: ReportPeriod
): T[] => {
  if (period.kind === 'season') return boxes;
  return boxes.filter((box) => {
    const etd = toDay(box.ship_etd);
    return Boolean(etd && etd >= period.from && etd <= period.to);
  });
};

type ShipmentGroup = {
  reference: string;
  receiver: string;
  kilos: number;
  identifiers: string[];
  ship_name: string | null;
  ship_type: string | null;
  ship_etd: string | null;
  ship_eta: string | null;
  ship_destination_port: string | null;
  ship_destination_country: string | null;
  tracking_journeys: ShipmentTrackingSummary[];
  kilosByVariety: Map<string, number>;
  kilosByLabel: Map<string, number>;
  kilosByCaliber: Map<string, number>;
  // Kilos de calibre 2J o mayor, por variedad.
  kilosTwoJUpByVariety: Map<string, number>;
  // Etiqueta → formato → kilos.
  kilosByLabelFormat: Map<string, Map<string, number>>;
};

const varietyOf = (box: ReportShipmentBox) =>
  cleanText(box.variety) || cleanText(box.labeled_variety) || '—';
const labelOf = (box: ReportShipmentBox) =>
  cleanText(box.box_label)?.toUpperCase() || '—';
const caliberOf = (box: ReportShipmentBox) =>
  cleanText(box.caliber)?.toUpperCase() || '—';
// "NO ENCONTRADO" es lo que queda cuando el formato no tiene mapeo.
const formatOf = (box: ReportShipmentBox) => {
  const format = cleanText(box.box_format)?.toUpperCase();
  return format && format !== 'NO ENCONTRADO' ? format : '—';
};
// "2J up" en cereza: 2J o mayor, también escrito 2JUP, 2J+, 3JD… "JUP" no
// entra: es J o mayor.
const isTwoJUp = (caliber: string) => /^[2-9]J/.test(caliber);

const addTo = (map: Map<string, number>, key: string, value: number) =>
  map.set(key, (map.get(key) ?? 0) + value);

const groupByReference = (boxes: ReportShipmentBox[]) => {
  const groups = new Map<string, ShipmentGroup>();
  for (const box of boxes) {
    const reference = box.reference_number?.trim() || WITHOUT_REFERENCE;
    let group = groups.get(reference);
    if (!group) {
      group = {
        reference,
        receiver: box.receiver?.trim() || '',
        kilos: 0,
        identifiers: [],
        ship_name: box.ship_name || null,
        ship_type: box.ship_type || null,
        ship_etd: box.ship_etd || null,
        ship_eta: box.ship_eta || null,
        ship_destination_port: box.ship_destination_port || null,
        ship_destination_country: box.ship_destination_country || null,
        tracking_journeys: [],
        kilosByVariety: new Map(),
        kilosByLabel: new Map(),
        kilosByCaliber: new Map(),
        kilosTwoJUpByVariety: new Map(),
        kilosByLabelFormat: new Map(),
      };
      groups.set(reference, group);
    }
    const kilos = (box.quantity || 0) * (box.box_weight || 0);
    group.kilos += kilos;
    const label = labelOf(box);
    const variety = varietyOf(box);
    const caliber = caliberOf(box);
    addTo(group.kilosByVariety, variety, kilos);
    addTo(group.kilosByLabel, label, kilos);
    addTo(group.kilosByCaliber, caliber, kilos);
    if (isTwoJUp(caliber)) addTo(group.kilosTwoJUpByVariety, variety, kilos);
    const byFormat = group.kilosByLabelFormat.get(label) || new Map();
    addTo(byFormat, formatOf(box), kilos);
    group.kilosByLabelFormat.set(label, byFormat);
    addUniqueShipmentIdentifier(group.identifiers, box.container_number);
    addUniqueShipmentIdentifier(group.identifiers, box.awb_number);
    for (const journey of box.tracking_journeys || []) {
      if (!group.tracking_journeys.some((j) => j.id === journey.id)) {
        group.tracking_journeys.push(journey);
      }
    }
  }
  return Array.from(groups.values());
};

export type ReportShipmentRow = {
  reference: string;
  identifiers: string[];
  receiver: string;
  arrived: boolean;
  // Ya zarpó. Lo que no zarpó solo lo cuenta el plan comercial.
  departed: boolean;
  transport: ShipmentTransport | null;
  vessel: string | null;
  origin: string | null;
  destination: string | null;
  etd: string | null;
  eta: string | null;
  // Fechas declaradas por la exportadora, las que usa Programa Comercial.
  plannedEtd: string | null;
  plannedEta: string | null;
  ceq: number;
  kilos: number;
  ceqByVariety: Map<string, number>;
  ceqByLabel: Map<string, number>;
  ceqByCaliber: Map<string, number>;
  ceqTwoJUpByVariety: Map<string, number>;
  ceqByLabelFormat: Map<string, Map<string, number>>;
};

const toCeq = (kilosByKey: Map<string, number>, kgPerCeq: number) =>
  new Map(Array.from(kilosByKey, ([key, kilos]) => [key, kilos / kgPerCeq]));

const toRow = (
  group: ShipmentGroup,
  today: Date,
  kgPerCeq: number
): ReportShipmentRow => {
  const status = getShipmentLogisticsStatus(group, today);
  const journey = group.tracking_journeys[0];
  const etd = toDay(journey?.live_etd || group.ship_etd);
  // Sin ETA cuenta como zarpado solo si su ETD ya pasó.
  const departed =
    status !== 'pendingDeparture' &&
    (status !== 'missingEta' || Boolean(etd && etd <= localDay(today)));

  const mode = journey?.mode;
  return {
    reference: group.reference,
    identifiers: group.identifiers,
    receiver: group.receiver,
    arrived: status === 'arrived',
    departed,
    transport:
      normalizeTransport(group.ship_type) ||
      (mode === 'ocean' ? 'M' : mode === 'air' ? 'A' : null),
    vessel: cleanText(
      journey?.latest_actual_event?.vessel_name || group.ship_name
    ),
    origin: cleanText(journey?.origin_name),
    // Puerto del tracking o del despacho; si el despacho no trae puerto, el país.
    destination: cleanText(
      journey?.destination_name ||
        group.ship_destination_port ||
        group.ship_destination_country
    ),
    etd,
    eta: toDay(journey?.live_eta || group.ship_eta),
    plannedEtd: toDay(group.ship_etd),
    plannedEta: toDay(group.ship_eta),
    ceq: group.kilos / kgPerCeq,
    kilos: group.kilos,
    ceqByVariety: toCeq(group.kilosByVariety, kgPerCeq),
    ceqByLabel: toCeq(group.kilosByLabel, kgPerCeq),
    ceqByCaliber: toCeq(group.kilosByCaliber, kgPerCeq),
    ceqTwoJUpByVariety: toCeq(group.kilosTwoJUpByVariety, kgPerCeq),
    ceqByLabelFormat: new Map(
      Array.from(group.kilosByLabelFormat, ([label, byFormat]) => [
        label,
        toCeq(byFormat, kgPerCeq),
      ])
    ),
  };
};

const emptyBucket = (): ShipmentReportBucket => ({ shipments: 0, ceq: 0 });
const emptySplit = (): ShipmentReportSplit => ({
  arrived: emptyBucket(),
  inTransit: emptyBucket(),
});

const addToSplit = (
  split: ShipmentReportSplit,
  row: ReportShipmentRow,
  ceq = row.ceq
) => {
  const bucket = row.arrived ? split.arrived : split.inTransit;
  bucket.shipments += 1;
  bucket.ceq += ceq;
};

const splitCeq = (split: ShipmentReportSplit) =>
  split.arrived.ceq + split.inTransit.ceq;

const compareDates = (left: string | null, right: string | null) => {
  if (left === right) return 0;
  if (!left) return 1;
  if (!right) return -1;
  return left < right ? -1 : 1;
};

const mostCommon = <T>(values: T[]): T | null => {
  const counts = new Map<T, number>();
  for (const value of values) counts.set(value, (counts.get(value) ?? 0) + 1);
  let best: T | null = null;
  let bestCount = 0;
  for (const [value, count] of counts) {
    if (count > bestCount) {
      best = value;
      bestCount = count;
    }
  }
  return best;
};

const buildPorts = (rows: ReportShipmentRow[]): ShipmentReportPort[] => {
  const ports = new Map<
    string,
    ShipmentReportPort & { transports: ShipmentTransport[] }
  >();
  for (const row of rows) {
    const name = row.destination || '—';
    const key = name.toUpperCase();
    const port = ports.get(key) || {
      name,
      transport: null,
      transports: [],
      ...emptySplit(),
    };
    addToSplit(port, row);
    if (row.transport) port.transports.push(row.transport);
    ports.set(key, port);
  }
  return Array.from(ports.values())
    .map(({ transports, ...port }) => ({
      ...port,
      transport: mostCommon(transports),
    }))
    .sort(
      (a, b) =>
        b.arrived.shipments +
          b.inTransit.shipments -
          (a.arrived.shipments + a.inTransit.shipments) ||
        a.name.localeCompare(b.name)
    );
};

// Mix por variedad, etiqueta o calibre, en cajas equivalentes. De más a menos
// cajas y, desde la séptima, juntas en "otras". Con un orden (los calibres)
// van todas, en ese orden.
const buildMix = (
  rows: ReportShipmentRow[],
  ceqByKey: (row: ReportShipmentRow) => Map<string, number>,
  order?: string[]
): ShipmentReportVariety[] => {
  const items = new Map<string, ShipmentReportVariety>();
  for (const row of rows) {
    for (const [label, ceq] of ceqByKey(row)) {
      const item = items.get(label) || { label, ...emptySplit() };
      addToSplit(item, row, ceq);
      items.set(label, item);
    }
  }
  if (order) {
    const rank = (label: string) => {
      const index = order.indexOf(label);
      return index === -1 ? order.length : index;
    };
    return Array.from(items.values()).sort(
      (a, b) =>
        rank(a.label) - rank(b.label) ||
        splitCeq(b) - splitCeq(a) ||
        a.label.localeCompare(b.label)
    );
  }
  const sorted = Array.from(items.values()).sort(
    (a, b) => splitCeq(b) - splitCeq(a) || a.label.localeCompare(b.label)
  );
  if (sorted.length <= MAX_VARIETIES + 1) return sorted;

  const others: ShipmentReportVariety = {
    label: '',
    isOthers: true,
    ...emptySplit(),
  };
  for (const variety of sorted.slice(MAX_VARIETIES)) {
    others.arrived.ceq += variety.arrived.ceq;
    others.inTransit.ceq += variety.inTransit.ceq;
  }
  return [...sorted.slice(0, MAX_VARIETIES), others];
};

// Suma a cada variedad del mix sus cajas eq. de calibre 2J o mayor; "otras"
// lleva las de las variedades que no salen.
const withTwoJUp = (
  varieties: ShipmentReportVariety[],
  rows: ReportShipmentRow[]
): ShipmentReportVariety[] => {
  const byVariety = new Map<string, number>();
  for (const row of rows) {
    for (const [variety, ceq] of row.ceqTwoJUpByVariety) {
      addTo(byVariety, variety, ceq);
    }
  }
  const listed = new Set(varieties.map((item) => item.label));
  const others = Array.from(byVariety)
    .filter(([variety]) => !listed.has(variety))
    .reduce((sum, [, ceq]) => sum + ceq, 0);
  return varieties.map((item) => ({
    ...item,
    twoJUp: item.isOthers ? others : (byVariety.get(item.label) ?? 0),
  }));
};

// Etiqueta × formato de caja, en cajas equivalentes. Formatos y etiquetas de
// más a menos cajas; desde el sexto formato van juntos en "otros" y desde la
// séptima etiqueta, en "otras".
const buildLabelFormatMatrix = (rows: ReportShipmentRow[]): LabelFormatMatrix => {
  const cells = new Map<string, Map<string, number>>();
  const byFormat = new Map<string, number>();
  const byLabel = new Map<string, number>();
  for (const row of rows) {
    for (const [label, formats] of row.ceqByLabelFormat) {
      const labelCells = cells.get(label) || new Map<string, number>();
      for (const [format, ceq] of formats) {
        addTo(labelCells, format, ceq);
        addTo(byFormat, format, ceq);
        addTo(byLabel, label, ceq);
      }
      cells.set(label, labelCells);
    }
  }
  const ranked = (totals: Map<string, number>) =>
    Array.from(totals)
      .sort(([a, left], [b, right]) => right - left || a.localeCompare(b))
      .map(([key]) => key);
  const allFormats = ranked(byFormat);
  const allLabels = ranked(byLabel);
  const formats =
    allFormats.length <= MAX_FORMATS + 1 ? allFormats : allFormats.slice(0, MAX_FORMATS);
  const hasOtherFormats = formats.length < allFormats.length;
  const labels =
    allLabels.length <= MAX_VARIETIES + 1 ? allLabels : allLabels.slice(0, MAX_VARIETIES);

  const sumFor = (labelKeys: string[], include: (format: string) => boolean) =>
    labelKeys.reduce(
      (sum, label) =>
        sum +
        Array.from(cells.get(label) ?? []).reduce(
          (labelSum, [format, ceq]) => labelSum + (include(format) ? ceq : 0),
          0
        ),
      0
    );
  const cellsFor = (labelKeys: string[]) => ({
    ceq: [
      ...formats.map((format) => sumFor(labelKeys, (f) => f === format)),
      ...(hasOtherFormats
        ? [sumFor(labelKeys, (f) => !formats.includes(f))]
        : []),
    ],
    total: sumFor(labelKeys, () => true),
  });

  const matrixRows: LabelFormatRow[] = labels.map((label) => ({
    label,
    ...cellsFor([label]),
  }));
  if (labels.length < allLabels.length) {
    matrixRows.push({
      label: '',
      isOthers: true,
      ...cellsFor(allLabels.slice(labels.length)),
    });
  }
  return { formats, hasOtherFormats, rows: matrixRows, totals: cellsFor(allLabels) };
};

const buildUpcoming = (
  rows: ReportShipmentRow[],
  todayKey: string
): UpcomingArrival[] => {
  const groups = new Map<string, UpcomingArrival>();
  for (const row of rows) {
    if (row.arrived) continue;
    const key = [row.vessel, row.eta, row.destination].join('|');
    const group = groups.get(key) || {
      vessel: row.vessel,
      transport: row.transport,
      origin: row.origin,
      destination: row.destination,
      eta: row.eta,
      daysToEta: row.eta ? daysBetween(todayKey, row.eta) : null,
      shipments: 0,
      ceq: 0,
    };
    group.shipments += 1;
    group.ceq += row.ceq;
    group.transport ||= row.transport;
    group.origin ||= row.origin;
    groups.set(key, group);
  }
  return Array.from(groups.values()).sort(
    (a, b) => compareDates(a.eta, b.eta) || b.shipments - a.shipments
  );
};

const isoDay = (ms: number) => new Date(ms).toISOString().slice(0, 10);
// Lunes de la semana (ISO) de una fecha YYYY-MM-DD.
const mondayOf = (day: string) => {
  const ms = Date.parse(`${day}T00:00:00Z`);
  return ms - ((new Date(ms).getUTCDay() + 6) % 7) * MILLISECONDS_PER_DAY;
};

// Por arribar día a día, de mañana a UPCOMING_DAYS días más, sin saltarse
// días vacíos.
const buildUpcomingDays = (
  upcoming: UpcomingArrival[],
  todayKey: string
): UpcomingDay[] => {
  const today = Date.parse(`${todayKey}T00:00:00Z`);
  return Array.from({ length: UPCOMING_DAYS }, (_, index) => {
    const date = isoDay(today + (index + 1) * MILLISECONDS_PER_DAY);
    const rows = upcoming.filter((row) => row.eta === date);
    return {
      date,
      shipments: rows.reduce((sum, row) => sum + row.shipments, 0),
      ceq: rows.reduce((sum, row) => sum + row.ceq, 0),
    };
  });
};

// Embarques, uno por referencia. Por defecto solo los ya zarpados; el plan
// comercial pide todos, como la pantalla de Programa Comercial.
export const buildShipmentRows = ({
  boxes,
  kgPerCeq,
  today,
  includeNotDeparted = false,
}: {
  boxes: ReportShipmentBox[];
  kgPerCeq: number;
  today: Date;
  includeNotDeparted?: boolean;
}): ReportShipmentRow[] =>
  groupByReference(boxes)
    .map((group) => toRow(group, today, kgPerCeq))
    .filter((row) => includeNotDeparted || row.departed);

type SectionBase = {
  rows: ReportShipmentRow[];
  today: Date;
  weekOf: (date: string) => number | null;
};

type SectionInput = SectionBase & {
  // Lunes (YYYY-MM-DD) de la semana de corte. Sin valor: la por defecto
  // (defaultDepartureWeek).
  recentWeekStart?: string | null;
};

// Semana de corte por defecto: la última semana calendario completa con
// zarpes, para no castigar el cumplimiento con una semana a medio cargar. Si
// todavía no hay una completa, la última con zarpes.
export const defaultDepartureWeek = <Week extends { end: string }>(
  weeks: Week[],
  today: Date
): Week | null => weeks.find((week) => week.end < localDay(today)) ?? weeks[0] ?? null;

// Semana de corte (por ETD): la elegida o la por defecto, con sus embarques.
const lastDepartureWeek = ({
  rows,
  today,
  weekOf,
  recentWeekStart,
}: SectionInput) => {
  const todayKey = localDay(today);
  const departed = rows.filter((row) => row.etd && row.etd <= todayKey);
  const mondays = Array.from(new Set(departed.map((row) => mondayOf(row.etd!))))
    .sort((a, b) => b - a)
    .map((monday) => ({ monday, end: isoDay(monday + 6 * MILLISECONDS_PER_DAY) }));
  const monday = recentWeekStart
    ? Date.parse(`${recentWeekStart}T00:00:00Z`)
    : (defaultDepartureWeek(mondays, today)?.monday ?? null);
  if (monday == null) return { week: null, monday: null, rows: [] };
  return {
    week: weekOf(isoDay(monday)),
    monday,
    rows: departed.filter((row) => mondayOf(row.etd!) === monday),
  };
};

// Días sin embarques nuevos desde los que se avisa que la base de despachos
// puede no estar al día.
const STALE_BASE_DAYS = 7;

// Aviso de base desactualizada: el embarque más nuevo cargado (de todos los
// clientes, también los que aún no zarpan) tiene ETD de hace más de
// STALE_BASE_DAYS días. Sin embarques con ETD no hay con qué comparar.
export const staleShipmentsBase = (
  boxes: ReportShipmentBox[],
  today: Date
): { latestEtd: string; days: number } | null => {
  const latestEtd = boxes
    .map((box) => toDay(box.ship_etd))
    .filter((etd): etd is string => Boolean(etd))
    .sort()
    .pop();
  if (!latestEtd) return null;
  const days = daysBetween(latestEtd, localDay(today));
  return days > STALE_BASE_DAYS ? { latestEtd, days } : null;
};

// Lunes (YYYY-MM-DD) de la semana de hoy, para la opción "Hasta hoy".
export const currentWeekStart = (today: Date) =>
  isoDay(mondayOf(localDay(today)));

// Semana de corte del reporte (la elegida o la por defecto), con su lunes y
// el último día que cuenta: su domingo o, si es la semana en curso, hoy.
export const reportCutoffWeek = (input: SectionInput) => {
  const { week, monday } = lastDepartureWeek(input);
  if (monday == null) return null;
  const sunday = isoDay(monday + 6 * MILLISECONDS_PER_DAY);
  const todayKey = localDay(input.today);
  return {
    week,
    start: isoDay(monday),
    end: sunday < todayKey ? sunday : todayKey,
  };
};

export type DepartureWeek = ShipmentReportBucket & {
  week: number;
  start: string;
  end: string;
};

// Semanas con zarpes hasta hoy, de la más reciente a la más antigua, para
// elegir la de últimos embarques.
export const listDepartureWeeks = ({
  rows,
  today,
  weekOf,
}: SectionBase): DepartureWeek[] => {
  const todayKey = localDay(today);
  const byMonday = new Map<number, ReportShipmentRow[]>();
  for (const row of rows) {
    if (!row.etd || row.etd > todayKey) continue;
    const monday = mondayOf(row.etd);
    byMonday.set(monday, [...(byMonday.get(monday) ?? []), row]);
  }
  return Array.from(byMonday)
    .sort((a, b) => b[0] - a[0])
    .flatMap(([monday, weekRows]) => {
      const week = weekOf(isoDay(monday));
      return week == null
        ? []
        : [
            {
              week,
              start: isoDay(monday),
              end: isoDay(monday + 6 * MILLISECONDS_PER_DAY),
              ...sumBucket(weekRows),
            },
          ];
    });
};

const sumBucket = (rows: ReportShipmentRow[]): ShipmentReportBucket => ({
  shipments: rows.length,
  ceq: rows.reduce((sum, row) => sum + row.ceq, 0),
});

export const buildShipmentsToDate = ({
  rows,
  today,
  weekOf,
  recentWeekStart,
  kgPerCeq,
  plan,
  caliberOrder = DEFAULT_CALIBER_ORDER,
  twoJUp = false,
  restOfClients,
}: SectionInput & {
  kgPerCeq: number;
  plan: CommercialPlanSectionData;
  // Calibres de la especie, de menor a mayor.
  caliberOrder?: string[];
  // Columna "2J up" en el mix por variedad (solo cereza).
  twoJUp?: boolean;
  restOfClients?: ShipmentsToDateData['restOfClients'];
}): ShipmentsToDateData => {
  const status = emptySplit();
  for (const row of rows) addToSplit(status, row);
  const lastWeek = lastDepartureWeek({ rows, today, weekOf, recentWeekStart });
  const kilos = rows.reduce((sum, row) => sum + row.kilos, 0);
  const varieties = buildMix(rows, (row) => row.ceqByVariety);
  // Sin calibres cargados la columna saldría en 0% y no diría nada.
  const hasCalibers = rows.some((row) =>
    Array.from(row.ceqByCaliber.keys()).some((caliber) => caliber !== '—')
  );

  return {
    kgPerCeq,
    totals: {
      shipments: rows.length,
      ceq: kilos / kgPerCeq,
      kilos,
      sea: rows.filter((row) => row.transport === 'M').length,
      air: rows.filter((row) => row.transport === 'A').length,
      vessels: new Set(
        rows
          .filter((row) => row.transport !== 'A' && row.vessel)
          .map((row) => row.vessel!.toUpperCase())
      ).size,
    },
    status,
    lastWeek:
      lastWeek.week == null
        ? null
        : { week: lastWeek.week, ...sumBucket(lastWeek.rows) },
    plan,
    ports: buildPorts(rows),
    varieties: twoJUp && hasCalibers ? withTwoJUp(varieties, rows) : varieties,
    labels: buildMix(rows, (row) => row.ceqByLabel),
    calibers: buildMix(rows, (row) => row.ceqByCaliber, caliberOrder),
    labelFormats: buildLabelFormatMatrix(rows),
    ...(restOfClients ? { restOfClients } : {}),
  };
};

export const buildUpcomingArrivals = ({
  rows,
  today,
}: SectionInput): UpcomingArrivalsData => {
  const todayKey = localDay(today);
  // Lo que llega en los próximos días, más lo que ya cumplió su ETA y el
  // tracking todavía no confirma como arribado.
  const upcoming = buildUpcoming(rows, todayKey).filter(
    (row) => row.daysToEta != null && row.daysToEta <= UPCOMING_DAYS
  );
  const pending = upcoming.filter((row) => (row.daysToEta ?? 0) <= 0);
  return {
    days: UPCOMING_DAYS,
    inTransit: sumBucket(rows.filter((row) => !row.arrived)),
    window: {
      shipments: upcoming.reduce((sum, row) => sum + row.shipments, 0),
      ceq: upcoming.reduce((sum, row) => sum + row.ceq, 0),
    },
    pending: pending.reduce((sum, row) => sum + row.shipments, 0),
    byDay: buildUpcomingDays(upcoming, todayKey),
    rows: upcoming,
  };
};

export const buildRecentShipments = (
  input: SectionInput
): RecentShipmentsData => {
  const { week, monday, rows } = lastDepartureWeek(input);
  const todayKey = localDay(input.today);
  return {
    week,
    start: monday == null ? null : isoDay(monday),
    end: monday == null ? null : isoDay(monday + 6 * MILLISECONDS_PER_DAY),
    totals: sumBucket(rows),
    rows: rows
      .map((row) => ({
        reference: row.reference,
        identifiers: row.identifiers,
        vessel: row.vessel,
        transport: row.transport,
        destination: row.destination,
        etd: row.etd,
        eta: row.eta,
        daysToEta: row.eta ? daysBetween(todayKey, row.eta) : null,
        arrived: row.arrived,
        ceq: row.ceq,
      }))
      .sort(
        (a, b) =>
          compareDates(a.etd, b.etd) || a.reference.localeCompare(b.reference)
      ),
  };
};
